import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Box, Grid, Typography } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';

import PageContainer from '../../components/layout/PageContainer';
import {
  FBoxContainer,
  FHButton,
  FHInputField,
  FHPageLink,
  FProgressBar,
} from '../../components/base';
import { claimsActions } from '../../store';
import { BULK_UNBILLABLE_PASSWORD } from './bulkUnbillable/constants';
import { getBulkUnbillablePasswordInfoText } from './bulkUnbillable/utils';

import type { RootState } from '../../store';

function DeleteClaimForm() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { loading: claimsLoading } = useSelector((state: RootState) => state.claims);

  const [claimId, setClaimId] = useState<string>('');
  const [password, setPassword] = useState<string>('');

  const isPasswordVerified = password === BULK_UNBILLABLE_PASSWORD;

  const handleSubmit = () => {
    if (isNaN(Number(claimId))) {
      toast.error('Claim ID must be a number', {
        toastId: 'claim-id-status-error',
      });
      return;
    }
    if (claimId) {
      dispatch(
        claimsActions.adminDeleteClaim({
          claimId: claimId.trim(),
          getAccessTokenSilently,
        }),
      );
    }
  };
  return (
    <PageContainer>
      {claimsLoading && <FProgressBar />}
      <Box sx={{ mt: 3 }}>
        <FHPageLink to="/admin" label="Admin Home" />
        <FBoxContainer
          dataTestId="deleteClaimForm"
          sx={{ borderRadius: 2, width: '100%', boxShadow: 'none' }}
        >
          <Grid container>
            <Grid item xs={12} md={12} lg={8}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box ml={1} sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                  <Typography textAlign={'left'} variant="h4">
                    Delete Claim
                  </Typography>
                </Box>
                <Box
                  sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mt: 1, mb: 2 }}
                />
                <Grid container p={0.5} spacing={1} alignItems="center">
                  <Grid item xs={2.5}>
                    <Typography textAlign={'left'}>Password:</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <FHInputField
                      inputProps={{
                        'data-testid': 'deleteClaimPasswordInput',
                      }}
                      type="password"
                      autoComplete="off"
                      inputHeight={40}
                      sx={{ boxShadow: 'none' }}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      error={!!password && !isPasswordVerified}
                      infoText={getBulkUnbillablePasswordInfoText(password)}
                    />
                  </Grid>
                </Grid>

                {/* The delete feature is hidden until the password is verified, so the
                    page exposes no deletion controls to a user who lands here without
                    the password. */}
                {isPasswordVerified && (
                  <>
                    <Grid container p={0.5} spacing={1} alignItems="center" sx={{ mt: 1 }}>
                      <Grid item xs={2.5}>
                        <Typography textAlign={'left'}>Claim ID:</Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <FHInputField
                          inputProps={{
                            'data-testid': 'claimIdInput',
                          }}
                          inputHeight={40}
                          sx={{ boxShadow: 'none' }}
                          value={claimId}
                          onChange={(e) => setClaimId(e.target.value)}
                        />
                      </Grid>
                    </Grid>

                    <FHButton
                      data-testid="deleteClaimButton"
                      variant="contained"
                      sx={{ mt: 2, width: '200px' }}
                      startIcon={<DeleteIcon />}
                      onClick={handleSubmit}
                      disabled={!claimId || claimsLoading}
                    >
                      Delete
                    </FHButton>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </FBoxContainer>
      </Box>
    </PageContainer>
  );
}

export default DeleteClaimForm;
